import React, { useState, useCallback } from 'react';
import { useToast, Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { getWordTranslation, ArticleWordTranslation, saveWordToCollection, markWordAsKnown } from '../services/smartReadApi';
import { playTTS } from '../services/ttsApi';
import { useAuth } from '../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

export interface UseWordTranslationProps {
  /** Source language of the text */
  sourceLanguage: string;
  /** Pre-loaded word translations (optional) */
  preloadedTranslations?: ArticleWordTranslation[];
}

export function useWordTranslation({ sourceLanguage, preloadedTranslations = [] }: UseWordTranslationProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Check if mobile - provide default value to avoid undefined
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;

  // Mobile drawer controls
  const { isOpen: isWordDrawerOpen, onOpen: onWordDrawerOpen, onClose: onWordDrawerClose } = useDisclosure();

  // State
  const [selectedWord, setSelectedWord] = useState<string | null>(null);
  const [selectedWordData, setSelectedWordData] = useState<ArticleWordTranslation | null>(null);
  const [isPlayingWord, setIsPlayingWord] = useState(false);
  const [isSavingWord, setIsSavingWord] = useState(false);
  const [isMarkingWord, setIsMarkingWord] = useState(false);

  // Debug logging for popup state
  React.useEffect(() => {
    if (selectedWord && selectedWordData) {
      console.log('WordTranslation Debug:', {
        selectedWord,
        hasSelectedWordData: !!selectedWordData,
        isMobile,
        isWordDrawerOpen,
        shouldShowDesktopPopup: !isMobile && selectedWord && selectedWordData,
        shouldShowMobileDrawer: isMobile && isWordDrawerOpen
      });
    }
  }, [selectedWord, selectedWordData, isMobile, isWordDrawerOpen]);

  // Handle word click
  const handleWordClick = useCallback((e: React.MouseEvent<HTMLSpanElement>, word: string) => {
    e.stopPropagation();
    if (!word) return;

    console.log('Word clicked:', word, 'isMobile:', isMobile);

    // Close popup if clicking on the same word again
    if (word === selectedWord) {
      setSelectedWord(null);
      setSelectedWordData(null);
      if (isMobile) {
        onWordDrawerClose();
      }
      return;
    }

    // Close any existing popup before showing a new one
    setSelectedWord(null);
    setSelectedWordData(null);
    if (isMobile) {
      onWordDrawerClose();
    }

    // Set the selected word immediately for better UX
    setSelectedWord(word);

    try {
      console.log('Looking for translation of word:', word, '(normalized:', word.toLowerCase(), ')');

      // Try to find the word in pre-loaded translations
      const exactMatch = preloadedTranslations?.find(w => w.word === word);
      const lowerCaseMatch = preloadedTranslations?.find(w => w.word.toLowerCase() === word.toLowerCase());
      const match = exactMatch || lowerCaseMatch;

      if (match) {
        console.log('Found exact match for word:', match);
        setSelectedWordData(match);
        // Open mobile drawer if on mobile
        if (isMobile) {
          console.log('Opening mobile drawer for word:', word);
          onWordDrawerOpen();
        } else {
          console.log('Should show desktop popup for word:', word);
        }
      } else {
        // Fetch translation if not in pre-loaded translations
        getWordTranslation(word, sourceLanguage)
          .then(translation => {
            console.log('Found translation from API:', translation);
            if (translation) {
              setSelectedWordData(translation);
              // Open mobile drawer if on mobile
              if (isMobile) {
                console.log('Opening mobile drawer for API word:', word);
                onWordDrawerOpen();
              } else {
                console.log('Should show desktop popup for API word:', word);
              }
            }
          })
          .catch(error => {
            console.error('Error fetching word translation:', error);
            toast({
              title: t('smartRead.translationError'),
              description: t('smartRead.translationErrorMessage'),
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          });
      }
    } catch (error) {
      console.error('Error fetching word translation:', error);
      toast({
        title: t('smartRead.translationError'),
        description: t('smartRead.translationErrorMessage'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [selectedWord, preloadedTranslations, sourceLanguage, isMobile, onWordDrawerOpen, onWordDrawerClose, t, toast]);

  // Handle playing word pronunciation
  const handlePlayPronunciation = useCallback(async (word: string) => {
    if (isPlayingWord) return;

    try {
      setIsPlayingWord(true);
      await playTTS(word, sourceLanguage);
    } catch (error) {
      console.error('Error playing pronunciation:', error);
      toast({
        title: t('smartRead.playbackError'),
        description: t('smartRead.unableToPlayPronunciation'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsPlayingWord(false);
    }
  }, [isPlayingWord, sourceLanguage, t, toast]);

  // Handle saving word to collection
  const handleSaveWord = useCallback(async (word: string) => {
    if (isSavingWord || !word || !user) return;

    try {
      setIsSavingWord(true);

      // Use the updated saveWordToCollection function
      const result = await saveWordToCollection(word, sourceLanguage.toLowerCase());

      if (result) {
        // Invalidate multiple query keys to ensure updates are reflected
        queryClient.invalidateQueries({ queryKey: ['words'] });
        queryClient.invalidateQueries({ queryKey: ['words-with-practice'] });
        queryClient.invalidateQueries({ queryKey: ['user-stats'] });
        toast({
          title: t('smartRead.wordSaved'),
          description: t('smartRead.addedToCollection'),
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error saving word:', error);
      toast({
        title: t('common.error'),
        description: t('errors.failedToSave'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSavingWord(false);
    }
  }, [isSavingWord, user, sourceLanguage, queryClient, t, toast]);

  // Handle marking word as known
  const handleMarkKnown = useCallback(async (word: string) => {
    if (isMarkingWord || !word || !user) return;

    try {
      setIsMarkingWord(true);
      console.log(`Marking word "${word}" (${sourceLanguage}) as 100% known`);

      // Save the word first (this will handle duplicates gracefully)
      const savedWord = await saveWordToCollection(word, sourceLanguage.toLowerCase());

      if (savedWord && savedWord.id) {
        // Now mark it as known
        const result = await markWordAsKnown(savedWord.id);

        if (result) {
          // Invalidate multiple query keys to ensure mastery updates are reflected
          queryClient.invalidateQueries({ queryKey: ['words'] });
          queryClient.invalidateQueries({ queryKey: ['words-with-practice'] });
          queryClient.invalidateQueries({ queryKey: ['play_history'] });
          queryClient.invalidateQueries({ queryKey: ['user-stats'] });
          toast({
            title: t('smartRead.wordMarked'),
            description: t('smartRead.updatedInCollection'),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
          });
        }
      }
    } catch (error) {
      console.error('Error marking word as known:', error);
      toast({
        title: t('common.error'),
        description: t('errors.failedToUpdate'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsMarkingWord(false);
    }
  }, [isMarkingWord, user, sourceLanguage, queryClient, t, toast]);

  // Close all popups
  const handleClosePopups = useCallback(() => {
    setSelectedWord(null);
    setSelectedWordData(null);
    if (isMobile) {
      onWordDrawerClose();
    }
  }, [isMobile, onWordDrawerClose]);

  return {
    // State
    selectedWord,
    selectedWordData,
    isPlayingWord,
    isSavingWord,
    isMarkingWord,
    isMobile,

    // Mobile drawer
    isWordDrawerOpen,
    onWordDrawerOpen,
    onWordDrawerClose,

    // Actions
    handleWordClick,
    handlePlayPronunciation,
    handleSaveWord,
    handleMarkKnown,
    handleClosePopups,
  };
}